import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { MouseEvent, useState } from "react"
import { PdfFile } from "../../../shared/src/types/Content"
import { useStore } from "../../../contexts/store"
import { PodClass } from "../../../classes/Pod"
import { Op_deletePdfFile } from "../../../shared/src/types/Ops"

import ConfirmDialog from "../../Elements/ConfirmDialog"
import DownloadOptions from "./DownloadOptions"
import Link from "../../Elements/Link"
import ReadingProgress from "../../Elements/ReadingProgress"
import SettingsDialog from "./SettingsDialog"
import UxIcon from "../../Elements/UxIcon"
import { FilePreview, MaterialItem, VisibilityWarning } from "./PodMaterialHelper"

import { Box, Divider, IconButton, Paper, useTheme } from "@mui/material"


const PodMaterialFile = ({clone, isEditMode, file}: {clone?: boolean, isEditMode?: boolean, file: PdfFile}) => {
  const { t } = useTranslation()
  const { opStore, podStore, sessionStore, uiStore } = useStore()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const materialView = uiStore.materialView
  const pod: PodClass | null = podStore.pod
  if(pod === null) return null

  const allowedDeleteFile = pod.isAllowed('deletePdfFile', file.nodeId)
  const allowedEditFile =  pod.isAllowed('editPdfFile', file.nodeId)

  const fileIsHidden = pod.getFileIsHidden(file)
  const deviceWidth = uiStore.deviceWidth
  const podId = pod.podId

  const handleDeleteFile = () => {
    if (sessionStore.session) {
      const op:Op_deletePdfFile = {
        op: 'deletePdfFile',
        podId: pod.podId,
        data: {
          nodeId: file.nodeId,
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userId: sessionStore.session.user.userId,
          userName: podStore.userPseudonym || '',
        }
      }
      opStore.doOp(op)
    }
  }


  if(isEditMode) return (
    <>
      <MaterialItem item={file} itemType={"file"}>
        {clone === undefined &&
        <>
          <FileStatus file={file} />
          {file.status === "complete" &&
            <VisibilityWarning hidden={fileIsHidden} folderIsHidden={fileIsHidden && !file.hidden} />
          }
          <Box sx={{display: "grid", gridTemplateColumns: "max-content max-content", gap: "5px"}}>
            {allowedDeleteFile &&
              <IconButton sx={{padding: "10px"}} size="small" onClick={() => setShowDeleteDialog(true)}>
                <UxIcon fontSize="small" id="delete" />
              </IconButton>
            }
            <EditFileButton allowedEditFile={allowedEditFile} file={file} pod={pod} />
          </Box>
        </>
        }
      </MaterialItem>
      <ConfirmDialog
        handleClose={() => { setShowDeleteDialog(false)} }
        info={t('Deleting this file will also delete all interactions and conversations it contains. It cannot be undone. Are you sure?') ||''}
        onOk={handleDeleteFile}
        open={showDeleteDialog}
        title={t('Permanently Delete the File {{fileName}}?', {fileName: file.name})}
      />
    </>
  )


  if(deviceWidth === "tiny" && materialView === "tiles") return (
    <Box sx={{padding: "0px 15px"}}>
      <Box sx={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: "15px",
        justifyContent: "end",
        padding: "10px 15px"
      }}>
        <Link noDecoration={true} to={`/pod/${podId}/pdf/${file.nodeId}/`} sx={{
          alignItems: "center",
          display: "grid",
          flexGrow: 1,
          gap: "10px",
          gridTemplateColumns: "max-content auto",
          opacity: file.hidden ? 0.5 : 1,
          wordWrap: "break-word"
        }}>
          <UxIcon id={"podFile"} fontSize="small" />
          <Box sx={{display: "grid", fontSize: "16px", padding: "5px", wordBreak: "break-word"}}>
            {file.name}
            <Box sx={{fontWeight: 300}}>
              {file.description}
            </Box>
          </Box>
        </Link>
      </Box>
      <Link sx={{}} noDecoration={true} to={`/pod/${podId}/pdf/${file.nodeId}/`}>
        <FilePreview file={file} padding={"10px 0"} width={200} />
      </Link>
      <Box sx={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        justifyContent: "end"
      }}>
        <FileStatus file={file} />
        <ReadingProgress nodeId={file.nodeId} />
        {file.status === "complete" &&
          <VisibilityWarning hidden={fileIsHidden} folderIsHidden={fileIsHidden && !file.hidden} />
        }
        <DownloadFile file={file} pod={pod} />
        <EditFileButton allowedEditFile={allowedEditFile} file={file} pod={pod} />
      </Box>
      <Divider />
    </Box>
  )


  return (
    <Paper elevation={1} sx={{
      borderLeft: "15px solid #f9f4f4",
      margin: "10px 15px",
      padding: "10px 10px 10px 0",
      ":hover": {
        borderColor: "primary.main",
        color: "primary.dark",
        cursor: file.status === 'complete' ? "pointer" : null
      }
    }}>
      <MaterialItem item={file} itemType={"file"} materialView={materialView} podId={podId}>
        <FileStatus file={file} />
        <ReadingProgress nodeId={file.nodeId} />
        {file.status === "complete" &&
          <VisibilityWarning hidden={fileIsHidden} folderIsHidden={fileIsHidden && !file.hidden} />
        }
        <DownloadFile file={file} pod={pod} />
        <EditFileButton allowedEditFile={allowedEditFile} file={file} pod={pod} />
      </MaterialItem>
    </Paper>
  )
}

export default observer(PodMaterialFile)

const FileStatus = observer(({file}: {file: PdfFile}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if(file.status === 'scheduled') return (
    <Box sx={{
      alignItems: "center",
      color: theme.palette.warning.main,
      display: "flex",
      flexWrap: "wrap",
      gap: "5px"
    }}>
      <UxIcon sx={{color: "warning"}} id="clock" />
      {t('Scheduled to be processed')}
    </Box>
  )
  else if(file.status === 'processing') return (
    <Box sx={{
      alignItems: "center",
      color: theme.palette.info.main,
      display: "flex",
      flexWrap: "wrap",
      gap: "5px"
    }}>
      <UxIcon id="clock" />
      {t('Processing')} ({Math.min(file.pages.length, file.nofPages)} / {file.nofPages})
    </Box>
  )
  else if(file.status === 'broken') return (
    <Box sx={{
      alignItems: "center",
      color: theme.palette.error.main,
      display: "flex",
      flexWrap: "wrap",
      gap: "5px"
    }}>
      <UxIcon id="clock" />
      {t('Broken')}
    </Box>
  )
  else return null
})


const EditFileButton = ({allowedEditFile, file, pod}: {allowedEditFile: boolean, file: PdfFile, pod: PodClass}) => {
  const [openSettings, setOpenSettings] = useState(false)

  const handleOpenSettings = (e: MouseEvent) => {
    setOpenSettings(true)
    e.stopPropagation()
  }

  if(allowedEditFile) return (
    <>
      <IconButton sx={{padding: "10px"}} disabled={file.status !== "complete"} size="small" onClick={(e: MouseEvent) => handleOpenSettings(e)}>
        <UxIcon fontSize="small" id="settings" />
      </IconButton>
      <SettingsDialog handleClose={() => setOpenSettings(false)} itemType="file" item={file} open={openSettings} pod={pod} />
    </>
  )
  return null
}

const DownloadFile = ({file, pod}: {file: PdfFile, pod: PodClass}) => {
  const [ showDownloadDialog, setShowDownloadDialog ] = useState(false)
  const podAllowsDownload = Boolean(pod.allowDownload.indexOf('pdf')>-1)

  const handleShowDownload = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setShowDownloadDialog(true)
  }

  if(podAllowsDownload) return (
    <>
      <IconButton onClick={handleShowDownload}>
        <UxIcon fontSize="small" id="download" />
      </IconButton>
      <DownloadOptions nodeId={file.nodeId} open={showDownloadDialog} handleClose={() => { setShowDownloadDialog(false)}} />
    </>
  )
  return null
}